import axios from 'axios';
import { notifyIfTermsRequired } from '../utils/terms';
import i18n from '../i18n';
import { hantMessages } from '../utils/hant';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 小工具：更稳的 FormData 判定（避免 instanceof 失效）
const isFormDataBody = (body) =>
  body &&
  typeof body === 'object' &&
  typeof body.append === 'function' &&
  (body[Symbol.toStringTag] === 'FormData' || body.constructor?.name === 'FormData');

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 界面语言 → 后端按此给常用错误码的文案（docs/I18N.md）
    config.headers['Accept-Language'] = i18n.language || 'zh-CN';
    
    // 如果是 FormData，移除所有层级上的 Content-Type 让浏览器自动设置 boundary
    if (isFormDataBody(config.data)) {
      if (config.headers) {
        delete config.headers['Content-Type'];
      }
      // axios 可能还会把它挂在 common/post 层级上，保险起见都删掉
      if (api.defaults.headers?.post) {
        delete api.defaults.headers.post['Content-Type'];
      }
      if (api.defaults.headers?.common) {
        delete api.defaults.headers.common['Content-Type'];
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 一次性登出并跳登录页：清 token + 防重复跳转（多路轮询同时 401/403 时只跳一次，
// 避免「疯狂重试刷红」）。
// reason 带到登录页提示原因（如 revoked：密码已在别处修改）。
let reauthInFlight = false;
const forceReauth = (reason) => {
  localStorage.removeItem('token');
  delete api.defaults.headers.common['Authorization'];
  if (!reauthInFlight && window.location.pathname !== '/login') {
    reauthInFlight = true;
    window.location.href = reason ? `/login?reason=${reason}` : '/login';
  }
};

// 响应拦截器
// 繁体界面：后端提示文字只有简体（少数错误码除外），在这里统一转繁体；数据字段不动
const toInterfaceScript = (data) => (i18n.language === 'zh-TW' ? hantMessages(data) : data);

api.interceptors.response.use(
  (response) => {
    response.data = toInterfaceScript(response.data);
    return response;
  },
  (error) => {
    if (error.response) error.response.data = toInterfaceScript(error.response.data);
    const status = error.response?.status;
    const code = error.response?.data?.code;
    // 令牌失效才登出：所有 401（缺失/过期/禁用/用户不存在）+ 403 且 code=INVALID_TOKEN
    //（JWT 密钥轮换后旧 token）。**不可**因权限类 403（VERIFICATION_REQUIRED /
    // PHONE_VERIFICATION_REQUIRED / 权限不足）登出——那是已登录但不满足动作门槛。
    const tokenInvalid =
      status === 401 || (status === 403 && code === 'INVALID_TOKEN');
    if (status === 401 && code === 'TOKEN_REVOKED') {
      // 本页刚改完密码时会换发新令牌：轮询请求若恰在换发前带着旧令牌发出，用新令牌重试一次，不登出
      const sent = String(error.config?.headers?.Authorization || '').replace(/^Bearer /, '');
      const current = localStorage.getItem('token');
      if (error.config && current && sent && sent !== current && !error.config._retriedWithNewToken) {
        error.config._retriedWithNewToken = true;
        return api(error.config);
      }
      forceReauth('revoked');
    } else if (tokenInvalid) {
      forceReauth();
    }
    // 关键操作要求先同意协议：广播给根组件 TermsModal 弹窗（不登出）
    notifyIfTermsRequired(error);
    return Promise.reject(error);
  }
);

export default api;
